import { Field, Form, Formik } from 'formik'
import { FC } from 'react'
import { DateField, TextField } from '@/shared/ui/FormFields'
import { Button } from '@/shared/ui/Button/Button'
import { Loader } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import LanguageTabs from '@/features/MultilingualTabs/ui/LanguageTabs'
import { generateLanguageTabsConfig } from '@/shared/lib/utils'
import { MultilingualEntityForm } from '@/features/MultilingualTabs/ui/forms/MultilingualEntityForm'
import { useEditWebinarForm } from '../lib/use-edit-webinar-form'
import { editWebinarFormSchema } from '../lib/consts'
import ImageCropProvider from '@/app/providers/ImageCropProvider'
import PhotoUploder from '@/features/Photos/UploadAndFrontCrop/PhotoUploader'

type EditWebinarFormProps = {
  currentWebinarId: number
  onCloseModal: () => void
}

export const EditWebinarForm: FC<EditWebinarFormProps> = props => {
  const { currentWebinarId, onCloseModal } = props
  const { t } = useTranslation(['webinar', 'common'])
  const { initialValues, isLoadingEditWebinar, isErrorWebinar, isLoadingWebinar, submitForm } =
    useEditWebinarForm(currentWebinarId, onCloseModal)

  const languageTabs = generateLanguageTabsConfig(
    isLoadingEditWebinar,
    MultilingualEntityForm,
    'webinar',
  )

  if (isErrorWebinar) {
    return <>{t('common:error_messages.something_went_wrong')}</>
  }

  if (isLoadingWebinar) {
    return (
      <div className='flex flex-col py-8 items-center justify-center'>
        <Loader size='xs' color='blue' />
      </div>
    )
  }
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={submitForm}
      enableReinitialize
      validationSchema={editWebinarFormSchema(t, languageTabs)}
    >
      <Form>
        <LanguageTabs config={languageTabs} />
        <div className='mt-[1rem]'>
          <DateField
            name='release_date'
            label={t('webinar:release_date')}
            className='flex flex-col w-full mb-4'
            disabled={isLoadingEditWebinar}
          />
          <TextField
            name='video_url'
            label={t('webinar:video_url')}
            className='flex flex-col w-full mb-4'
            required
            disabled={isLoadingEditWebinar}
          />
          <Field name='thumbnail'>
            {({ form: { values, setFieldValue } }) => (
              <ImageCropProvider>
                <PhotoUploder
                  label={t('webinar:thumbnail')}
                  initialValue={values.thumbnail}
                  name={'thumbnail'}
                  setField={setFieldValue}
                  aspectRetio={4 / 3}
                />
              </ImageCropProvider>
            )}
          </Field>
        </div>
        <div className='flex flex-row w-full'>
          <Button type='submit' size='md' color='base' disabled={isLoadingEditWebinar}>
            {isLoadingEditWebinar ? (
              <Loader size='xs' color='white' />
            ) : (
              t('webinar:buttons.save_changes')
            )}
          </Button>
        </div>
      </Form>
    </Formik>
  )
}
